import { gql } from '@apollo/client';

// ==================== MEMBERSHIPS ====================
export const CREATE_MEMBERSHIP = gql`
  mutation CreateMembership($input: CreateMembershipInput!) {
    createMembership(input: $input) {
      id
      role
      isActive
      user {
        id
        email
        firstName
        lastName
      }
      organization {
        id
        name
      }
      parkingIds
    }
  }
`;

export const UPDATE_MEMBERSHIP = gql`
  mutation UpdateMembership($id: ID!, $input: UpdateMembershipInput!) {
    updateMembership(id: $id, input: $input) {
      id
      role
      isActive
      parkingIds
    }
  }
`;

export const REVOKE_MEMBERSHIP = gql`
  mutation RevokeMembership($id: ID!) {
    revokeMembership(id: $id)
  }
`;

// ==================== STAFF SESSIONS ====================
export const CREATE_STAFF_SESSION = gql`
  mutation CreateStaffSession($input: CreateStaffSessionInput!) {
    createStaffSession(input: $input) {
      id
      status
      sessionType
      billingType
      discountPercent
      plateNumber
      notes
      reservedAt
      location {
        id
        name
        code
      }
      user {
        id
        email
      }
      createdByStaff {
        id
        email
      }
    }
  }
`;

export const END_STAFF_SESSION = gql`
  mutation EndStaffSession($sessionId: ID!) {
    endStaffSession(sessionId: $sessionId) {
      id
      status
      endedAt
      finalFee
    }
  }
`;

export const CANCEL_STAFF_SESSION = gql`
  mutation CancelStaffSession($sessionId: ID!, $reason: String) {
    cancelStaffSession(sessionId: $sessionId, reason: $reason) {
      id
      status
      notes
    }
  }
`;

export const UPDATE_SESSION_DISCOUNT = gql`
  mutation UpdateSessionDiscount($sessionId: ID!, $discountPercent: Int!, $reason: String) {
    updateSessionDiscount(sessionId: $sessionId, discountPercent: $discountPercent, reason: $reason) {
      id
      billingType
      discountPercent
      notes
    }
  }
`;

export const UPDATE_SESSION_PLATE = gql`
  mutation UpdateSessionPlate($sessionId: ID!, $plateNumber: String!) {
    updateSessionPlate(sessionId: $sessionId, plateNumber: $plateNumber) {
      id
      plateNumber
    }
  }
`;

export const UPDATE_SESSION_NOTES = gql`
  mutation UpdateSessionNotes($sessionId: ID!, $notes: String!) {
    updateSessionNotes(sessionId: $sessionId, notes: $notes) {
      id
      notes
    }
  }
`;

// ==================== PAYMENT COLLECTION ====================
export const CONFIRM_PAYMENT_COLLECTED = gql`
  mutation ConfirmPaymentCollected($sessionId: ID!) {
    confirmPaymentCollected(sessionId: $sessionId) {
      success
      message
      session {
        id
        status
        paidAt
      }
      alreadyCollectedBy
      collectedAt
    }
  }
`;

export const MARK_PAYMENT_NOT_COLLECTED = gql`
  mutation MarkPaymentNotCollected($sessionId: ID!) {
    markPaymentNotCollected(sessionId: $sessionId) {
      success
      message
      session {
        id
        status
      }
      alreadyCollectedBy
      collectedAt
    }
  }
`;

// ==================== USERS ====================
export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      email
      firstName
      lastName
      role
      organization {
        id
        name
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      email
      firstName
      lastName
      role
      enabled
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

// ==================== ORGANIZATIONS ====================
export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
      name
      email
      phone
      revenueShareRate
    }
  }
`;

export const UPDATE_ORGANIZATION = gql`
  mutation UpdateOrganization($id: ID!, $input: UpdateOrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      id
      name
      email
      phone
      revenueShareRate
      enabled
    }
  }
`;

export const DELETE_ORGANIZATION = gql`
  mutation DeleteOrganization($id: ID!) {
    deleteOrganization(id: $id)
  }
`;

// ==================== PARKINGS ====================
export const CREATE_PARKING = gql`
  mutation CreateParking($input: CreateParkingInput!) {
    createParking(input: $input) {
      id
      name
      code
      type
      organization {
        id
        name
      }
    }
  }
`;

export const UPDATE_PARKING = gql`
  mutation UpdateParking($id: ID!, $input: UpdateParkingInput!) {
    updateParking(id: $id, input: $input) {
      id
      name
      code
      enabled
    }
  }
`;

export const DELETE_PARKING = gql`
  mutation DeleteParking($id: ID!) {
    deleteParking(id: $id)
  }
`;

// ==================== SECTORS ====================
export const CREATE_SECTOR = gql`
  mutation CreateSector($input: CreateSectorInput!) {
    createSector(input: $input) {
      id
      name
      code
      type
      parent {
        id
        name
      }
    }
  }
`;

export const UPDATE_SECTOR = gql`
  mutation UpdateSector($id: ID!, $input: UpdateSectorInput!) {
    updateSector(id: $id, input: $input) {
      id
      name
      code
      enabled
    }
  }
`;

export const DELETE_SECTOR = gql`
  mutation DeleteSector($id: ID!) {
    deleteSector(id: $id)
  }
`;

// ==================== SLOTS ====================
export const CREATE_SLOT = gql`
  mutation CreateSlot($input: CreateSlotInput!) {
    createSlot(input: $input) {
      id
      name
      code
      serialNumber
      deviceId
      type
      parent {
        id
        name
      }
    }
  }
`;

export const UPDATE_SLOT = gql`
  mutation UpdateSlot($id: ID!, $input: UpdateSlotInput!) {
    updateSlot(id: $id, input: $input) {
      id
      name
      code
      serialNumber
      deviceId
      enabled
    }
  }
`;

export const DELETE_SLOT = gql`
  mutation DeleteSlot($id: ID!) {
    deleteSlot(id: $id)
  }
`;

// ==================== PRICING TEMPLATES ====================
export const CREATE_PRICING_TEMPLATE = gql`
  mutation CreatePricingTemplate($input: CreatePricingTemplateInput!) {
    createPricingTemplate(input: $input) {
      id
      name
      baseRate
      currency
      pricingMode
      enabled
      assignmentCount
    }
  }
`;

export const UPDATE_PRICING_TEMPLATE = gql`
  mutation UpdatePricingTemplate($id: ID!, $input: UpdatePricingTemplateInput!) {
    updatePricingTemplate(id: $id, input: $input) {
      id
      name
      baseRate
      currency
      pricingMode
      enabled
      assignmentCount
    }
  }
`;

export const DELETE_PRICING_TEMPLATE = gql`
  mutation DeletePricingTemplate($id: ID!) {
    deletePricingTemplate(id: $id)
  }
`;

// ==================== PRICING ASSIGNMENTS ====================
export const CREATE_PRICING_ASSIGNMENT = gql`
  mutation CreatePricingAssignment($input: CreatePricingAssignmentInput!) {
    createPricingAssignment(input: $input) {
      id
      validFrom
      validUntil
      enabled
      priority
      location {
        id
        name
        type
      }
      pricingTemplate {
        id
        name
      }
    }
  }
`;

export const UPDATE_PRICING_ASSIGNMENT = gql`
  mutation UpdatePricingAssignment($id: ID!, $input: UpdatePricingAssignmentInput!) {
    updatePricingAssignment(id: $id, input: $input) {
      id
      validFrom
      validUntil
      enabled
      priority
      pricingTemplate {
        id
        name
      }
    }
  }
`;

export const DELETE_PRICING_ASSIGNMENT = gql`
  mutation DeletePricingAssignment($id: ID!) {
    deletePricingAssignment(id: $id)
  }
`;

// ==================== DEVICE COMMANDS ====================
export const SEND_DEVICE_COMMAND = gql`
  mutation SendDeviceCommand($deviceId: Int!, $command: String!) {
    sendDeviceCommand(deviceId: $deviceId, command: $command) {
      success
      message
      deviceId
      commandSent
      expectedTime
    }
  }
`;
